import {
  getDatasetAnalytics,
  type DatasetAnalyticsResp,
  type DatasetAnalyticsTrendBucket,
} from "@/api";
import { resolveLoadErrorMessage } from "@/components/dataset/loadState";
import { createLogger } from "@/logging";

const logger = createLogger("components/analytics");

/**
 * 버킷 하나의 로드 상태다. `data` 는 마지막 성공 결과이므로 다시 조회하는 중에도 남는다.
 * 그래서 화면이 스켈레톤으로 돌아가지 않는다. `errorMessage` 는 마지막 조회가 실패했을 때만 있다.
 */
export type DatasetAnalyticsEntry = {
  data: DatasetAnalyticsResp | null;
  loading: boolean;
  errorMessage: string | null;
};

export type DatasetAnalyticsStoreSnapshot = Readonly<
  Record<DatasetAnalyticsTrendBucket, DatasetAnalyticsEntry>
>;

const IDLE_ENTRY: DatasetAnalyticsEntry = { data: null, loading: false, errorMessage: null };

/**
 * 데이터셋 횡단 통계(`GET /v1/datasets/analytics`)의 전역 저장소다.
 *
 * 대시보드 카드와 검색 콤보박스의 추천 라벨(건수 정렬)이 같은 응답을 쓴다. 화면마다 따로 받으면
 * 한 세션에서 같은 조직 통계를 여러 번 요청한다. 조직 누적 통계는 초 단위로 바뀌지 않는다.
 * 그래서 **폴링하지 않는다**. 첫 요청에 한 번 받고, 사람이 누른 새로고침(`refresh`)에만 다시 받는다.
 *
 * 버킷은 수집 추이 축만 구분하고, 나머지 축의 값은 같다. 그래서 버킷별로 따로 캐시한다.
 * 추천처럼 추이를 보지 않는 소비처는 어느 버킷을 읽어도 된다(`anyData`).
 *
 * 소유: 로드·캐시·실패 상태, 같은 버킷의 중복 요청 차단. 비소유: 표시(로딩·stale 문구)와
 * 버킷 선택. 소비처가 정한다.
 */
export class DatasetAnalyticsStore {
  private snapshot: DatasetAnalyticsStoreSnapshot = { day: IDLE_ENTRY, week: IDLE_ENTRY };
  private readonly listeners = new Set<() => void>();
  private readonly inFlight = new Set<DatasetAnalyticsTrendBucket>();

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): DatasetAnalyticsStoreSnapshot => this.snapshot;

  /** 아직 받지 않은 버킷이면 요청한다. 이미 있거나 받는 중이면 아무것도 하지 않는다. */
  ensure(bucket: DatasetAnalyticsTrendBucket): void {
    const entry = this.snapshot[bucket];
    if (entry.data !== null || entry.loading) {
      return;
    }
    void this.load(bucket);
  }

  /**
   * 캐시를 버리고 다시 받는다(사람이 누른 새로고침). 보고 있는 버킷은 새 값이 도착할 때까지
   * 직전 값을 유지한다. 다른 버킷의 캐시는 비운다. 그래서 다음에 전환할 때 새로 받는다.
   * 새로고침 뒤 추이 축이 버킷마다 다른 시점의 값을 보이면 안 된다.
   */
  refresh(bucket: DatasetAnalyticsTrendBucket): Promise<void> {
    for (const other of ["day", "week"] as const) {
      if (other !== bucket && !this.inFlight.has(other) && this.snapshot[other] !== IDLE_ENTRY) {
        this.publish(other, IDLE_ENTRY);
      }
    }
    return this.load(bucket);
  }

  /** 추이를 보지 않는 소비처가 쓴다. 어느 버킷이든 받아 둔 응답 하나를 반환한다. 없으면 null. */
  anyData(): DatasetAnalyticsResp | null {
    return this.snapshot.week.data ?? this.snapshot.day.data;
  }

  // 테스트 전용이다. 모듈 싱글턴이므로 케이스 사이에 상태가 남지 않게 초기화한다.
  resetForTest(): void {
    this.listeners.clear();
    this.inFlight.clear();
    this.snapshot = { day: IDLE_ENTRY, week: IDLE_ENTRY };
  }

  private async load(bucket: DatasetAnalyticsTrendBucket): Promise<void> {
    if (this.inFlight.has(bucket)) {
      return;
    }
    this.inFlight.add(bucket);
    this.publish(bucket, { ...this.snapshot[bucket], loading: true, errorMessage: null });
    try {
      const data = await getDatasetAnalytics(bucket);
      this.publish(bucket, { data, loading: false, errorMessage: null });
    } catch (error: unknown) {
      logger.error("Failed to load dataset analytics.", error);
      this.publish(bucket, {
        data: this.snapshot[bucket].data,
        loading: false,
        errorMessage: resolveLoadErrorMessage(error),
      });
    } finally {
      this.inFlight.delete(bucket);
    }
  }

  private publish(bucket: DatasetAnalyticsTrendBucket, entry: DatasetAnalyticsEntry): void {
    this.snapshot = { ...this.snapshot, [bucket]: entry };
    for (const listener of this.listeners) {
      listener();
    }
  }
}

export const datasetAnalyticsStore = new DatasetAnalyticsStore();
